import { Fragment } from 'react'
import { Table } from 'react-bootstrap'
import Skeleton from '@mui/material/Skeleton'

const resolveRowKey = (rowKey, row, index) =>
  typeof rowKey === 'function' ? rowKey(row, index) : row?.[rowKey]

const resolveRowKeys = (rows, rowKey) => {
  const seenKeys = new Set()

  return rows.map((row, index) => {
    const key = resolveRowKey(rowKey, row, index)
    if ((typeof key !== 'string' && typeof key !== 'number') || key === '') {
      throw new Error(
        `UbiquaDataTable requires a non-empty string or number rowKey for row ${index}.`
      )
    }

    const normalizedKey = String(key)
    if (seenKeys.has(normalizedKey)) {
      throw new Error(
        `UbiquaDataTable requires unique rowKey values (duplicate: ${normalizedKey}).`
      )
    }

    seenKeys.add(normalizedKey)
    return normalizedKey
  })
}

/**
 * Tabla base de escritorio para pantallas de datos.
 *
 * Datos que recibe:
 * - `columns` (requerido): arreglo de columnas. Cada una requiere `{ key, label }`.
 *   Opcionalmente acepta `render(row, index)` para dibujar JSX personalizado,
 *   `align: 'center'`, `width`, `description`, `className`, `headerClassName`
 *   y `headerRender(column)`.
 * - `rows` (requerido): arreglo de objetos. Si una columna no tiene `render`,
 *   se muestra `row[column.key]`.
 * - `rowKey`: campo único y estable de cada fila (por defecto `id`) o función
 *   `(row, index) => key`. Debe devolver un string o número no vacío; nunca el índice.
 * - `renderActions(row, index)` (opcional): devuelve los botones/JSX de la
 *   columna final. Si no se envía, la columna de acciones no se crea.
 * - `onRowClick(row, index)` (opcional): acción al presionar una fila.
 * - `isRowExpanded(row, index)` y `renderExpandedRow(row, index)` (opcionales):
 *   agregan una fila de detalle. La pantalla controla selección y consulta.
 * - `loading`: muestra tres filas animadas como DataTable.
 * - `emptyContent` y `loadingContent`: contenido vacío y carga personalizada.
 * - `minWidth` y `className`: ajustes visuales puntuales sin duplicar el CSS global.
 *
 * Los filtros, la paginación, las tarjetas móviles y la consulta de datos deben
 * vivir en la pantalla que consume este componente.
 */
export default function UbiquaDataTable({
  ariaLabel = 'Tabla',
  columns = [],
  rows = [],
  rowKey = 'id',
  renderActions,
  actionColumnLabel = 'Acciones',
  actionColumnWidth,
  emptyContent = 'No hay resultados',
  loading = false,
  loadingContent = null,
  onRowClick,
  isRowExpanded,
  renderExpandedRow,
  className = '',
  minWidth
}) {
  const hasActions = typeof renderActions === 'function'
  const colSpan = columns.length + (hasActions ? 1 : 0)
  const rowKeys = loading ? [] : resolveRowKeys(rows, rowKey)

  return (
    <div className="ubiqua-data-table-shell">
      <div className="ubiqua-data-table-card">
        <div className="ubiqua-data-table-scroll">
          <Table
            hover
            responsive
            aria-label={ariaLabel}
            aria-busy={loading}
            className={`ubiqua-data-table ${className}`.trim()}
            style={minWidth ? { '--ubiqua-table-min-width': minWidth } : undefined}
          >
            <thead className="ubiqua-data-table__head">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={
                      column.headerClassName || (column.align === 'center' ? 'text-center' : '')
                    }
                    style={column.width ? { width: column.width } : undefined}
                    title={column.description}
                  >
                    {column.headerRender ? column.headerRender(column) : column.label}
                  </th>
                ))}
                {hasActions ? (
                  <th
                    className="text-center"
                    style={actionColumnWidth ? { width: actionColumnWidth } : undefined}
                  >
                    {actionColumnLabel}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                loadingContent ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center">
                      {loadingContent}
                    </td>
                  </tr>
                ) : (
                  Array.from({ length: 3 }, (_, index) => (
                    <tr key={`skeleton-${index}`} aria-hidden="true">
                      {Array.from({ length: colSpan }, (_, columnIndex) => (
                        <td key={columnIndex}>
                          <Skeleton animation="wave" height={20} />
                        </td>
                      ))}
                    </tr>
                  ))
                )
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-center">
                    {emptyContent}
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => (
                  <Fragment key={rowKeys[rowIndex]}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                      style={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      {columns.map((column) => {
                        const value = column.render
                          ? column.render(row, rowIndex)
                          : (row?.[column.key] ?? '—')

                        return (
                          <td
                            key={column.key}
                            className={
                              column.className || (column.align === 'center' ? 'text-center' : '')
                            }
                            style={column.width ? { width: column.width } : undefined}
                          >
                            {value}
                          </td>
                        )
                      })}
                      {hasActions ? (
                        <td
                          className="text-center"
                          style={actionColumnWidth ? { width: actionColumnWidth } : undefined}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {renderActions(row, rowIndex)}
                        </td>
                      ) : null}
                    </tr>
                    {renderExpandedRow && isRowExpanded?.(row, rowIndex) ? (
                      <tr className="ubiqua-data-table__expanded-row">
                        <td colSpan={colSpan}>{renderExpandedRow(row, rowIndex)}</td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  )
}
